// SaveValidationResult/GetValidationResult payload (FE-defined JSON, BE stores verbatim):
// summary plus issues, never rows. engineVersion invalidates snapshots when rules change.

import { TemplateKind } from "../../../templateConfig/validationRuleTypes";
import { ValidationIssue, ValidationRunSummary } from "../engine/validationTypes";

const VALIDATION_PAYLOAD_VERSION = 1;

// Larger issue lists are dropped from the checkpoint (counts only) to keep the request body small.
export const MAX_PERSISTED_ISSUES = 10000;

// Bump when rule configs or engine semantics change enough that an old snapshot's counts
// can no longer be trusted for submit-readiness.
export const VALIDATION_ENGINE_VERSION = 6;

export type ValidationResultPayload = {
  version: number;
  engineVersion: number;
  templateType: TemplateKind;
  validatedAt: string;
  sourceFiles: Array<{ submissionFileID?: string; fileName: string }>;
  summary: ValidationRunSummary;
  // Absent on counts-only snapshots.
  issues?: ValidationIssue[];
};

export function buildValidationResultPayload(
  templateType: TemplateKind,
  summary: ValidationRunSummary,
  sourceFiles: Array<{ submissionFileID?: string; fileName: string }>,
  issues?: ValidationIssue[]
): ValidationResultPayload {
  return {
    version: VALIDATION_PAYLOAD_VERSION,
    engineVersion: VALIDATION_ENGINE_VERSION,
    templateType,
    validatedAt: new Date().toISOString(),
    sourceFiles,
    summary,
    issues: issues && issues.length <= MAX_PERSISTED_ISSUES ? issues : undefined,
  };
}

// The payload is posted as a JSON string, but the generated model types validationResult as
// `any` — accept either shape so a BE that deserialises on store still round-trips.
export function parseValidationResultPayload(stored: unknown): ValidationResultPayload {
  const payload = (typeof stored === "string" ? JSON.parse(stored) : stored) as ValidationResultPayload;
  if (payload.version !== VALIDATION_PAYLOAD_VERSION) {
    throw new Error(`Unsupported validation payload version: ${payload.version}`);
  }
  return { ...payload, summary: { ...payload.summary, relationships: payload.summary.relationships ?? [] } };
}
